import { LitElement, html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { registerFCM, onFcmTokenRefresh, onForegroundMessage, getCurrentFcmToken, getIdToken, getLastFcmError } from '../lib/firebase.js';
import { serverEvents, LIST_CHANGE_CODES, type LifecycleEvent, type ServerEvent } from '../lib/events.js';
import { clearOutletViewCaches, invalidateOutletView } from '../lib/outlet-view-cache.js';
import { DEFAULT_THEME, FONT_OPTIONS, THEME_VARS } from '../lib/theme.js';
import type { OutletTheme } from '../lib/theme.js';
import {
  formatExpiryDateMs,
  subscriptionState,
  type OutletSubscriptionInfo,
  type SubscriptionState,
} from '../lib/subscription.js';
import { siteBrand, mallBaseUrl } from '../lib/site-config.js';
import './stock-search.js';
import './outlet-panel.js';
import './stores-dashboard.js';
import './stock-manage.js';
import './reports-page.js';
import './member-manage.js';
import './company-manage.js';
import './mall-orders.js';
import './sales-page.js';
import './wholesales-page.js';
import './purchases-page.js';
import './accounting-page.js';
import './chart-of-account.js';
import './billing-page.js';

type OwnedOutlet = {
  code: string;
  name: string;
  masterDB: string;
  transDB: string;
  hostAddress: string;
  hostPort: number;
  hostName: string;
  outletName: string;
  accessAs: string[];
  renewalOnly?: boolean;
  expiredReasons?: string[];
  /** Live DB-hosting + gateway subscription state from the gateway. */
  subscription?: OutletSubscriptionInfo;
};

/**
 * Transaction lists and the kept-view entry their server events invalidate.
 * The rows shown by these panels come from the same ppServer events the
 * desktop list windows reload on (ServerEventMapping: sales → receipt list,
 * purchase → invoice list, holds → suspended lists).
 */
const TRADE_LIST_VIEWS: Record<string, { scope: string; key: (store: string) => string }> = {
  sales: { scope: 'trade-records', key: (store) => `${store}|sales|cash` },
  salesHold: { scope: 'trade-records', key: (store) => `${store}|hold|cash` },
  salesWholesales: { scope: 'trade-records', key: (store) => `${store}|sales|credit` },
  salesWholesalesHold: { scope: 'trade-records', key: (store) => `${store}|hold|credit` },
  purchase: { scope: 'trade-purchases', key: (store) => `${store}|receive` },
  purchaseHold: { scope: 'trade-purchases', key: (store) => `${store}|hold` },
};

@customElement('app-shell')
export class AppShell extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100dvh;
      overflow: hidden;
    }
    header {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      padding: max(0.5rem, env(safe-area-inset-top)) 0.9rem 0.55rem;
      background: var(--color-primary);
      background-image: linear-gradient(
        135deg,
        color-mix(in srgb, var(--color-primary) 86%, #ffffff) 0%,
        var(--color-primary) 45%,
        color-mix(in srgb, var(--color-primary) 80%, #001a4d) 100%
      );
      color: #fff;
      position: sticky;
      top: 0;
      z-index: 30;
      flex-shrink: 0;
      box-shadow: 0 2px 12px rgba(15, 35, 80, 0.22);
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .header-center {
      flex: 1;
      text-align: center;
      font-size: 1rem;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 0 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 40px;
    }
    .header-center .outlet-none {
      opacity: 0.6;
      font-style: italic;
    }
    .header-outlet { display: inline-flex; align-items: center; gap: 0.5rem; min-width: 0; }
    .header-outlet .ho-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
    header .status-pill.online { background: rgba(255,255,255,0.2); color: #d9f5de; }
    header .status-pill.offline { background: rgba(255,255,255,0.16); color: #eceff1; }
    .header-banner-frame {
      border: none;
      width: 100%;
      height: 100%;
      max-height: 48px;
      background: transparent;
    }
    .banner-upload-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      border: none;
      border-radius: 11px;
      background: rgba(255,255,255,0.16);
      color: #fff;
      font-size: 1rem;
      cursor: pointer;
      flex-shrink: 0;
      transition: background 0.15s;
    }
    .banner-upload-btn:hover {
      background: rgba(255,255,255,0.3);
    }
    .banner-upload-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .selected-db-badge {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      background: rgba(255,255,255,0.15);
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 0.8rem;
      white-space: nowrap;
    }
    .selected-db-badge .db-icon { font-size: 0.9rem; }
    .selected-db-badge .db-name { font-weight: 500; }
    .selected-db-badge .db-code { opacity: 0.7; font-size: 0.7rem; }
    .selected-db-badge .db-deselect {
      background: none;
      border: none;
      color: #fff;
      font-size: 1rem;
      cursor: pointer;
      padding: 0 2px;
      line-height: 1;
      opacity: 0.7;
    }
    .selected-db-badge .db-deselect:hover { opacity: 1; }
    .btn-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 38px; height: 38px;
      border: none; border-radius: 11px;
      background: rgba(255,255,255,0.16); color: #fff;
      font-size: 1.1rem; cursor: pointer; transition: background 0.15s;
    }
    .btn-icon:hover { background: rgba(255,255,255,0.3); }
    .btn-menu {
      display: none;
      align-items: center; justify-content: center;
      width: 42px; height: 42px;
      border: none; border-radius: 12px;
      background: rgba(255,255,255,0.16); color: #fff;
      font-size: 1.25rem; cursor: pointer; line-height: 1;
      transition: background 0.15s;
    }
    .btn-menu:hover { background: rgba(255,255,255,0.3); }

    /* ── overlay backdrop ── */
    .overlay {
      display: none;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.4);
      z-index: 20;
    }

    /* ── body: sidebar + content ── */
    .body {
      display: flex;
      flex: 1;
      min-height: 0;
    }

    /* ── sidebar ── */
    .sidebar {
      width: 276px;
      flex-shrink: 0;
      background: var(--color-surface);
      border-right: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      z-index: 25;
      position: relative;
    }
    .sidebar-header {
      padding: 0.75rem 1rem;
      font-size: 0.8rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--color-text-secondary);
      border-bottom: 1px solid var(--color-border);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .sidebar-header .btn-close-sidebar {
      display: none;
      background: none; border: none; color: var(--color-text-secondary);
      font-size: 1.2rem; cursor: pointer; padding: 0 4px; line-height: 1;
    }
    .sidebar-list {
      flex: 1;
      overflow-y: auto;
      padding: 0.5rem;
    }
    .sidebar .store-card {
      cursor: pointer;
      padding: 0.65rem 0.75rem;
      border-radius: var(--radius);
      border: 2px solid transparent;
      margin-bottom: 0.35rem;
      transition: all 0.15s;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }
    .sidebar .store-card:hover {
      background: #f1f3f4;
    }
    .sidebar .store-card.selected {
      border-color: var(--color-primary);
      background: #e8f0fe;
    }
    .sidebar .store-card-all {
      cursor: pointer;
      padding: 0.65rem 0.75rem;
      border-radius: var(--radius);
      border: 2px solid transparent;
      margin-bottom: 0.5rem;
      font-size: 0.85rem;
      font-weight: 500;
      color: var(--color-text-secondary);
      transition: all 0.15s;
      border-bottom: 1px solid var(--color-border);
      padding-bottom: 0.75rem;
      margin-bottom: 0.5rem;
    }
    .sidebar .store-card-all:hover {
      background: #f1f3f4;
    }
    .sidebar .store-card-all.selected {
      border-color: var(--color-primary);
      background: #e8f0fe;
      color: var(--color-primary);
    }
    .sidebar .store-card .store-name {
      font-size: 0.85rem;
      font-weight: 500;
      margin-bottom: 2px;
    }
    .sidebar .store-card .store-card-info {
      flex: 1;
      min-width: 0;
    }
    .store-card-settings {
      background: none;
      border: none;
      color: var(--color-text-secondary);
      font-size: 0.85rem;
      cursor: pointer;
      padding: 2px 4px;
      line-height: 1;
      border-radius: 4px;
      flex-shrink: 0;
      opacity: 0.5;
      transition: opacity 0.15s, color 0.15s;
    }
    .sidebar .store-card:hover .store-card-settings,
    .sidebar .store-card.selected .store-card-settings {
      opacity: 0.8;
    }
    .store-card-settings:hover {
      opacity: 1 !important;
      color: var(--color-primary);
      background: rgba(26,115,232,0.1);
    }
    .sidebar .store-card .store-meta {
      font-size: 0.7rem;
      color: var(--color-text-secondary);
    }
    .sidebar .store-card .store-roles {
      display: flex;
      flex-wrap: wrap;
      gap: 2px;
      margin-top: 4px;
    }
    .sidebar .store-card .store-roles span {
      background: var(--color-primary);
      color: #fff;
      padding: 1px 6px;
      border-radius: 8px;
      font-size: 0.62rem;
      white-space: nowrap;
    }
    .sidebar-footer {
      padding: 0.6rem 0.75rem;
      border-top: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0.45rem;
      font-size: 0.75rem;
      color: var(--color-text-secondary);
      flex-shrink: 0;
    }
    .sidebar-footer .sf-user {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }
    .sidebar-footer .sf-avatar {
      flex: 0 0 28px;
      width: 28px; height: 28px;
      border-radius: 50%;
      background: #e8f0fe;
      color: var(--color-primary);
      display: flex; align-items: center; justify-content: center;
      font-size: 14px;
    }
    .sidebar-footer .footer-email {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
      color: var(--color-text);
      font-size: 0.78rem;
    }
    .sidebar-footer .btn-logout {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      width: 100%;
      padding: 6px 9px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      background: transparent;
      color: var(--color-text-secondary);
      font-family: inherit;
      font-size: 0.76rem;
      font-weight: 600;
      line-height: 1.2;
      cursor: pointer;
      transition: color 0.15s, border-color 0.15s, background 0.15s;
    }
    .sidebar-footer .btn-logout:hover {
      color: var(--color-error, #d93025);
      border-color: currentColor;
      background: rgba(217, 48, 37, 0.06);
    }
    .sidebar-empty {
      padding: 1rem;
      text-align: center;
      color: var(--color-text-secondary);
      font-size: 0.8rem;
    }

    /* ── welcome / outlet picker (main panel) ── */
    .welcome { max-width: 960px; margin: 0 auto; padding: 0.25rem; }
    .welcome h2 { margin: 0 0 0.3rem; font-size: 1.3rem; color: var(--color-text); }
    .welcome-sub { margin: 0 0 1.1rem; color: var(--color-text-secondary); font-size: 0.88rem; }
    .outlet-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 0.75rem; }
    .outlet-card {
      display: flex; flex-direction: column; gap: 0.7rem;
      padding: 0.95rem; text-align: left;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: 16px;
      box-shadow: 0 1px 3px rgba(15, 35, 80, 0.06);
      cursor: pointer; font-family: inherit;
      transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s;
    }
    .outlet-card:hover { box-shadow: 0 10px 26px rgba(15, 35, 80, 0.14); transform: translateY(-2px); border-color: var(--color-primary); }
    .outlet-card:active { transform: scale(0.99); }
    .outlet-card.offline .store-avatar { filter: grayscale(0.85); opacity: 0.8; }
    .outlet-card.expiring { border-color: #f0c36d; background: #fff8e1; }
    .outlet-card.expired { border-color: #8c1d18; background: #b3261e; }
    .outlet-card.expired .oc-name,
    .outlet-card.expired .oc-sub { color: #fff; }
    .outlet-card.expired .status-pill { background: rgba(255,255,255,0.16); color: #fff; }
    .outlet-card.expired .status-pill .dot { background: #fff; }
    .outlet-card.expired .oc-role { background: rgba(255,255,255,0.16); color: #fff; }
    .outlet-card .oc-top { display: flex; align-items: center; gap: 0.6rem; min-width: 0; }
    .outlet-card .oc-id { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
    .outlet-card .oc-name { font-size: 0.92rem; font-weight: 700; color: var(--color-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .outlet-card .oc-sub { font-size: 0.72rem; color: var(--color-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .outlet-card .oc-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 0.35rem; }
    .oc-role {
      background: #eef3fb;
      background: color-mix(in srgb, var(--color-primary) 10%, #fff);
      color: var(--color-primary);
      border-radius: 999px; padding: 2px 9px;
      font-size: 0.66rem; font-weight: 700;
    }
    .outlet-card.ghost { background: transparent; border-style: dashed; box-shadow: none; }
    .outlet-card.ghost:hover { background: #f9fbff; }
    .outlet-card.ghost .store-avatar {
      background: #eef3fb;
      background: color-mix(in srgb, var(--color-primary) 10%, #fff);
      color: var(--color-primary);
      font-size: 1rem;
    }

    /* ── right panel ── */
    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    /* ── sidebar tree ── */
    .tree { user-select: none; }
    .tree-node {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      width: 100%;
      text-align: left;
      padding: 7px 8px;
      background: none;
      border: none;
      border-radius: 10px;
      font-size: 0.84rem;
      color: var(--color-text);
      cursor: pointer;
      transition: background 0.15s;
    }
    .tree-node:hover { background: #f4f7fb; }
    .tree-node.selected { background: #e8f0fe; color: var(--color-primary); font-weight: 600; }
    .chev {
      flex-shrink: 0;
      width: 24px; height: 24px;
      display: flex; align-items: center; justify-content: center;
      background: none; border: none; border-radius: 7px;
      color: var(--color-text-secondary); cursor: pointer; padding: 0;
    }
    .chev:hover { background: rgba(26,115,232,0.1); color: var(--color-primary); }
    .chev svg { width: 14px; height: 14px; transition: transform 0.18s ease; }
    .chev.open svg { transform: rotate(90deg); }
    .tree-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .tree-root {
      margin-bottom: 0.5rem;
      padding: 0.6rem;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      font-weight: 700;
      font-size: 0.86rem;
    }
    .tree-root:hover { background: #f4f7fb; }
    .tree-root.selected { background: #e8f0fe; border-color: rgba(26,115,232,0.35); }
    .tree-root .tree-icon {
      width: 30px; height: 30px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 9px; font-size: 0.95rem;
      background: #eef3fb;
      background: color-mix(in srgb, var(--color-primary) 10%, #fff);
    }

    /* ── outlet cards (sidebar) ── */
    .store-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.55rem 0.6rem;
      margin-bottom: 2px;
      border: 1px solid transparent;
      border-radius: 12px;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
    }
    .store-item:hover { background: #f4f7fb; }
    .store-item.selected { background: #e8f0fe; border-color: rgba(26,115,232,0.35); }
    /* DB-hosting expiry (independent of the gateway-subscription badge) */
    .store-item.expiring { background: #fff8e1; border-color: #f0c36d; }
    .store-item.expiring .store-name { color: #7a5b00; }
    .store-item.expired,
    .store-item.expired.selected { background: #b3261e; border-color: #8c1d18; }
    .store-item.expired .store-name,
    .store-item.expired .store-sub { color: #fff; }
    .store-item.expired .store-avatar {
      background: #fff;
      background-image: none;
      color: #b3261e;
    }
    .store-item.expired .chev { color: #fff; }
    .store-avatar {
      flex-shrink: 0;
      width: 34px; height: 34px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 10px;
      font-size: 0.74rem; font-weight: 800; letter-spacing: 0.02em;
      color: #fff;
      background: var(--color-primary);
      background: linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 55%, #06215a));
    }
    .store-item.offline .store-avatar { filter: grayscale(0.85); opacity: 0.8; }
    .store-item.offline .store-name { color: var(--color-text-secondary); }
    .store-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
    .store-name { font-size: 0.85rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .store-sub { font-size: 0.68rem; color: var(--color-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .store-item .status-pill { font-size: 0.62rem; padding: 2px 7px; }

    .tree-children {
      margin: 2px 0 8px 0.75rem;
      padding-left: 0.5rem;
      border-left: 2px solid #eef1f5;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .tree-group-label {
      padding: 2px 10px 4px;
      font-size: 0.66rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--color-text-secondary);
      opacity: 0.85;
    }
    .tree-sep {
      height: 0;
      margin: 6px 8px;
      border-top: 1px solid var(--color-border, #eef1f5);
    }
    .tree-child {
      padding: 8px 10px;
      font-size: 0.83rem;
      gap: 0.5rem;
    }
    .tree-child .tree-icon {
      width: 22px; height: 22px;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.85rem;
      border-radius: 7px;
      background: #f1f4f9;
      flex-shrink: 0;
    }
    .tree-child.selected .tree-icon { background: rgba(26,115,232,0.14); }

    /* ── outlet availability (live) ── */
    .status-pill {
      display: inline-flex; align-items: center; gap: 0.3rem;
      padding: 2px 9px; border-radius: 999px;
      font-size: 0.68rem; font-weight: 700; line-height: 1.6;
      white-space: nowrap; flex-shrink: 0;
    }
    .status-pill .dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
    .status-pill.online { background: rgba(52,168,83,0.13); color: #1e8e3e; }
    .status-pill.online .dot { background: var(--color-success, #34a853); box-shadow: 0 0 0 3px rgba(52,168,83,0.18); }
    .status-pill.offline { background: rgba(95,99,104,0.12); color: #5f6368; }
    .status-pill.offline .dot { background: #9aa0a6; }
    .status-pill.expired { background: rgba(234,67,53,0.12); color: #c5221f; }
    .status-pill.expired .dot { background: #ea4335; }
    .status-pill.expiring { background: rgba(240,173,78,0.22); color: #7a5b00; }
    .status-pill.expiring .dot { background: #f0ad4e; box-shadow: 0 0 0 3px rgba(240,173,78,0.2); }
    .store-item.expired .status-pill { background: rgba(255,255,255,0.16); color: #fff; }
    .store-item.expired .status-pill .dot { background: #fff; box-shadow: none; }

    /* gateway-subscription badge — separate from the outlet tint */
    .gw-pill {
      flex-shrink: 0; border-radius: 999px; padding: 1px 6px;
      font-size: 0.58rem; font-weight: 700; white-space: nowrap;
    }
    .gw-pill.expiring { background: #fff3cd; color: #7a5b00; }
    .gw-pill.expired { background: #ffebee; color: #b3261e; }

    /* right-panel tint follows DB hosting only */
    .right-panel.expiring main { background: #fff8e1; }
    .right-panel.expired main { background: #fdecea; }
    .expiry-banner {
      display: flex; align-items: center; gap: 0.5rem;
      border-radius: 12px; padding: 0.55rem 0.8rem;
      font-size: 0.84rem; margin-bottom: 0.6rem;
    }
    .expiry-banner.expiring { background: #fff3cd; border: 1px solid #f0c36d; color: #6b4f00; }
    .expiry-banner.expired { background: #b3261e; border: 1px solid #8c1d18; color: #fff; }
    .expiry-banner > span:nth-child(2) { flex: 1; }
    .expiry-action {
      border: 0; border-radius: 8px; padding: 0.3rem 0.7rem;
      font-size: 0.78rem; font-weight: 700; cursor: pointer; flex-shrink: 0;
    }
    .expiry-banner.expiring .expiry-action { background: #b7791f; color: #fff; }
    .expiry-banner.expired .expiry-action { background: #fff; color: #b3261e; }
    .offline-banner {
      display: flex; align-items: center; gap: 0.5rem;
      background: #fff8e1;
      border: 1px solid #ffe082;
      color: #8d6e63;
      border-radius: 12px;
      padding: 0.6rem 0.85rem;
      font-size: 0.84rem;
      margin-bottom: 0.6rem;
    }
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--color-text-secondary);
      text-align: center;
      padding: 2rem;
    }
    .empty-state-icon { font-size: 2.5rem; margin-bottom: 0.5rem; }
    .empty-state h3 { margin: 0 0 0.35rem; color: var(--color-text); }
    .empty-state p { margin: 0; font-size: 0.85rem; max-width: 360px; }
    .empty-cta {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      margin-top: 1rem;
      padding: 0.6rem 1.1rem;
      border: none;
      border-radius: 999px;
      background: var(--color-primary);
      color: #fff;
      font-family: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 6px 18px rgba(26, 115, 232, 0.25);
      transition: background 0.15s;
    }
    .empty-cta:hover { background: #1557b0; }
    main {
      flex: 1;
      padding: 1rem;
      overflow-y: auto;
      overflow-x: hidden;
      min-height: 0;
    }

    /* ── mobile: sidebar as overlay ── */
    @media (max-width: 768px) {
      .btn-menu {
        display: block;
      }
      .sidebar {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        width: 280px;
        max-width: 85vw;
        z-index: 35;
        transform: translateX(-100%);
        transition: transform 0.25s ease;
        box-shadow: 2px 0 12px rgba(0,0,0,0.3);
      }
      .sidebar.open {
        transform: translateX(0);
      }
      .sidebar-header .btn-close-sidebar {
        display: block;
      }
      .overlay.open {
        display: block;
      }
      header {
        padding: max(0.5rem, env(safe-area-inset-top)) 0.75rem 0.5rem;
      }
      .btn-menu, .btn-icon {
        min-width: 44px; min-height: 44px;
        display: inline-flex; align-items: center; justify-content: center;
      }
      main { padding: 0.5rem; }
      .sidebar-footer { padding-bottom: calc(0.6rem + env(safe-area-inset-bottom)); }
      .tree-node { min-height: 44px; font-size: 0.92rem; }
      .chev { width: 32px; height: 32px; }
      .chev svg { width: 16px; height: 16px; }
      .store-item { min-height: 48px; }
      .store-card-settings, .sidebar-header .btn-close-sidebar {
        min-width: 40px; min-height: 40px;
        display: inline-flex; align-items: center; justify-content: center;
      }
      .sidebar-footer .btn-logout { min-height: 40px; padding: 8px 12px; }
      .modal-overlay { align-items: flex-end; }
      .modal {
        width: 100%; max-width: none; max-height: 90dvh;
        border-radius: 18px 18px 0 0;
      }
      .modal-body { padding-bottom: calc(1.25rem + env(safe-area-inset-bottom)); }
      .modal-body select { min-height: 48px; font-size: 16px; }
    }

    /* ── Registration modal ── */
    .modal-overlay {
      display: none;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.5);
      z-index: 50;
      align-items: center;
      justify-content: center;
    }
    .modal-overlay.open {
      display: flex;
    }
    .modal {
      background: var(--color-surface);
      border-radius: 12px;
      width: 90%;
      max-width: 480px;
      max-height: 85vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 8px 32px rgba(0,0,0,0.2);
    }
    .modal-header {
      padding: 1rem 1.25rem;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .modal-header h3 { margin: 0; font-size: 1.1rem; }
    .modal-close {
      background: none; border: none; font-size: 1.3rem;
      cursor: pointer; color: var(--color-text-secondary);
      padding: 0 4px; line-height: 1;
    }
    .modal-body {
      padding: 1.25rem;
      overflow-y: auto;
      flex: 1;
    }
    .modal-body .form-group {
      margin-bottom: 1rem;
    }
    .modal-body label {
      display: block;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--color-text-secondary);
      margin-bottom: 0.35rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .modal-body select {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      font-size: 0.9rem;
      background: var(--color-surface);
      cursor: pointer;
    }
    .modal-body select:focus {
      outline: none;
      border-color: var(--color-primary);
    }
    .modal-body input[type="color"] {
      width: 100%;
      height: 40px;
      padding: 4px 8px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      background: var(--color-surface);
      cursor: pointer;
    }
    .modal-body store-option {
      padding: 0.65rem 0.75rem;
      border: 2px solid var(--color-border);
      border-radius: 8px;
      cursor: pointer;
      margin-bottom: 0.35rem;
      transition: all 0.15s;
    }
    .modal-body .store-option:hover {
      border-color: var(--color-primary);
      background: #f0f4ff;
    }
    .modal-body .store-option.selected {
      border-color: var(--color-primary);
      background: #e8f0fe;
    }
    .modal-body .store-option .s-name {
      font-size: 0.85rem;
      font-weight: 500;
    }
    .modal-body .store-option .s-meta {
      font-size: 0.7rem;
      color: var(--color-text-secondary);
    }
    .role-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .role-chip {
      min-height: 38px;
      display: inline-flex; align-items: center;
      padding: 6px 16px;
      border: 2px solid var(--color-border);
      border-radius: 999px;
      font-family: inherit;
      font-size: 0.82rem;
      font-weight: 600;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s;
      background: var(--color-surface);
    }
    .role-chip:hover {
      border-color: var(--color-primary);
    }
    .role-chip.selected {
      border-color: var(--color-primary);
      background: var(--color-primary);
      color: #fff;
    }
    .modal-footer {
      padding: 1rem 1.25rem;
      border-top: 1px solid var(--color-border);
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }
    .btn-cancel {
      display: inline-flex; align-items: center; justify-content: center;
      min-height: 44px; padding: 8px 20px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      background: var(--color-surface);
      cursor: pointer;
      font-family: inherit;
      font-size: 0.88rem;
      font-weight: 600;
    }
    .btn-submit {
      display: inline-flex; align-items: center; justify-content: center;
      min-height: 44px; padding: 8px 20px;
      border: none;
      border-radius: 12px;
      background: var(--color-primary);
      color: #fff;
      cursor: pointer;
      font-family: inherit;
      font-size: 0.88rem;
      font-weight: 600;
    }
    .btn-submit:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .btn-cancel:hover { background: #f1f3f4; }
    .btn-submit:hover { background: #1557b0; }
    .modal-error {
      color: var(--color-error);
      font-size: 0.8rem;
      margin-top: 0.5rem;
    }
    .modal-success {
      color: var(--color-success);
      font-size: 0.8rem;
      margin-top: 0.5rem;
    }
    .sidebar-register-btn {
      display: flex; align-items: center; justify-content: center; gap: 0.4rem;
      width: 100%;
      min-height: 44px;
      padding: 8px 12px;
      border: 1px dashed var(--color-border);
      border-radius: 12px;
      background: transparent;
      color: var(--color-primary);
      font-family: inherit;
      font-size: 0.84rem;
      font-weight: 600;
      cursor: pointer;
      margin-top: 0.6rem;
      transition: background 0.15s, border-color 0.15s;
    }
    .sidebar-register-btn:hover {
      background: #e8f0fe;
      border-color: var(--color-primary);
    }
    .sidebar-section-title {
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--color-text-secondary);
      padding: 0.5rem 0.75rem 0.25rem;
    }
  `;

  @property({ type: String }) email = '';
  @property({ type: String }) sessionKey = '';
  @property({ type: Array }) ownedDBs: OwnedOutlet[] = [];

  /** Re-render tick so expiry colours flip at midnight without a request. */
  @state() private _expiryTick = 0;

  /** Main panel rendered for the current tree selection. Null = nothing selected yet. */
  @state() private _panel: 'portfolio' | 'stocksearch' | 'home' | 'stocks' | 'sales' | 'wholesales' | 'purchases' | 'members' | 'customers' | 'suppliers' | 'salesrep' | 'promoter' | 'report' | 'mallorders' | 'accounting' | 'chartofaccount' | 'billing' | null = null;
  @state() private _billingFocus = '';
  /** Outlet whose subscription modal should open on the next render. */
  @state() private _billingRequest = '';
  /** Which outlet nodes are expanded in the sidebar tree. */
  @state() private _expandedOutlets: Record<string, boolean> = {};
  @state() private _rootExpanded = true;
  @state() private _sidebarOpen = false;
  /** serverId → true while that outlet's tunnel server is disconnected.
   *  Fed by the gateway's server_online/server_offline lifecycle broadcasts,
   *  so outlet availability updates live without logging out and back in. */
  @state() private _offlineServers: Record<string, boolean> = {};

  // Registration modal state
  @state() private _regModalOpen = false;
  @state() private _regStores: Array<{
    code: string;
    name: string;
    masterDB: string;
    transDB: string;
    hostAddress: string;
    hostPort: number;
    hostName: string;
    hostId: number;
  }> = [];
  /** All publicly accessible stores — shown in the "All Stores" dropdown group. */
  @state() private _publicStores: Array<{
    code: string;
    name: string;
    masterDB: string;
    transDB: string;
    hostAddress: string;
    hostPort: number;
    hostName: string;
    hostId: number;
  }> = [];
  @state() private _regSelectedStore: string = ''; // store code
  @state() private _regSelectedRole: string = '';
  @state() private _regLoading = false;
  @state() private _regError = '';
  @state() private _regSuccess = '';
  @state() private _regLoadingStores = false;
  @state() private _regNeedsApproval = false;

  // Approvals state
  @state() private _approvalsModalOpen = false;
  @state() private _approvalsAsOwner: Array<{
    id: string;
    requesterEmail: string;
    entityType: string;
    storeCode: string;
    storeName: string;
    status: 'pending' | 'approved' | 'rejected';
    createdAt: string;
    resolvedAt?: string;
  }> = [];
  @state() private _approvalsAsRequester: Array<{
    id: string;
    entityType: string;
    storeCode: string;
    storeName: string;
    status: 'pending' | 'approved' | 'rejected';
    createdAt: string;
    resolvedAt?: string;
  }> = [];
  @state() private _approvalsLoading = false;
  @state() private _approvalsActionError = '';

  @state() private _selectedDB: OwnedOutlet | null = null;

  @state() private _bannerHtml = '';
  @state() private _bannerExists = false;
  @state() private _bannerUploading = false;

  // ── Theme ──
  @state() private _currentTheme: OutletTheme | null = null;
  @state() private _themeModalOpen = false;
  @state() private _themeDraft: OutletTheme = { ...DEFAULT_THEME };
  @state() private _themeLoading = false;
  @state() private _themeSaving = false;
  @state() private _themeError = '';

  /** Map EntityType enum names to human-readable labels. */
  private _accessLabel(et: string): string {
    const map: Record<string, string> = {
      ET_User: 'User',
      ET_Member: 'Member',
      ET_SalesRep: 'Sales Rep',
      ET_SalesPromoter: 'Promoter',
      ET_Customer: 'Customer',
      ET_OnlineCustomer: 'Online Customer',
      ET_Supplier: 'Supplier',
      ET_OnlineSupplier: 'Online Supplier',
      ET_Unanymous: 'Anonymous',
      ET_Admin: 'Admin',
      ET_Notify: 'Notifications',
      ET_Dashboard: 'Dashboard',
    };
    return map[et] || et;
  }

  /** Two-letter monogram for store avatars. */
  private _initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  }

  /** Rotating chevron used by the sidebar tree rows. */
  private _chevron(open: boolean, onClick: (e: Event) => void) {
    return html`
      <button class="chev ${open ? 'open' : ''}" aria-label="Toggle" @click=${onClick}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 6 15 12 9 18"/>
        </svg>
      </button>`;
  }

  /** Landing panel shown before an outlet/panel is picked. */
  private _renderWelcome() {
    if (this._registeredOutlets.length === 0) {
      return html`
        <div class="empty-state">
          <div class="empty-state-icon">🗂️</div>
          <h3>No outlets yet</h3>
          <p>This account isn't linked to an outlet. You can still shop online — the storefront opens with this same sign-in.</p>
          <button class="empty-cta" @click=${this._openMall}>
            🛍️ Shop the ${siteBrand()} storefront
          </button>
        </div>`;
    }
    return html`
      <div class="welcome">
        <h2>Choose an outlet</h2>
        <p class="welcome-sub">Open a store to see its dashboard, stocks, members, reports and more.</p>
        <div class="outlet-grid">
          ${this._registeredOutlets.map((db) => {
            const online = this._isOutletOnline(db);
            const expired = !!db.renewalOnly;
            const accent = this._accentClass(db);
            const allRoles = db.accessAs || [];
            const roles = allRoles.slice(0, 3);
            const extra = allRoles.length - roles.length;
            const statusText = expired ? 'Expired — renew' : this._dbStatusText(db);
            return html`
              <button class="outlet-card ${accent} ${online ? '' : 'offline'}"
                data-testid="outlet-card"
                data-outlet-code=${db.code}
                @click=${() => this._selectOutletPanel(db, 'home')}>
                <span class="oc-top">
                  <span class="store-avatar">${this._initials(db.name)}</span>
                  <span class="oc-id">
                    <span class="oc-name">${db.name}</span>
                    <span class="oc-sub">${db.outletName || db.hostName || db.code}</span>
                  </span>
                </span>
                <span class="oc-meta">
                  <span class="status-pill ${this._pillClass(db)}"><span class="dot"></span>${statusText}</span>
                  ${this._gatewayBadge(db)}
                  ${roles.map((r) => html`<span class="oc-role">${this._accessLabel(r)}</span>`)}
                  ${extra > 0 ? html`<span class="oc-role">+${extra}</span>` : nothing}
                </span>
              </button>`;
          })}
          ${this._dashboardOutlets.length > 0 ? html`
            <button class="outlet-card ghost" data-testid="welcome-overview" @click=${this._selectRoot}>
              <span class="oc-top">
                <span class="store-avatar">📊</span>
                <span class="oc-id">
                  <span class="oc-name">Store Overview</span>
                  <span class="oc-sub">Financial health of all your outlets</span>
                </span>
              </span>
              <span class="oc-meta"><span class="oc-role">All outlets</span></span>
            </button>
          ` : nothing}
          <button class="outlet-card ghost" data-testid="welcome-find-item" @click=${this._selectFindItem}>
            <span class="oc-top">
              <span class="store-avatar">🔎</span>
              <span class="oc-id">
                <span class="oc-name">Find Item</span>
                <span class="oc-sub">Search stock across all your stores</span>
              </span>
            </span>
            <span class="oc-meta"><span class="oc-role">All outlets</span></span>
          </button>
        </div>
      </div>`;
  }

  private async _handleLogout() {
    serverEvents.disconnect();
    this._stopTradeListInvalidation();
    try { await api.logout(); } catch {}
    api.setSessionKey(null);
    // Forget kept per-outlet panel screens so the next user starts clean.
    clearOutletViewCaches();
    this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true }));
  }

  private _toggleSidebar() {
    this._sidebarOpen = !this._sidebarOpen;
  }

  private _closeSidebar() {
    this._sidebarOpen = false;
  }

  /**
   * Open the public storefront carrying this login.
   *
   * The storefront is a separate origin, so it cannot see this Firebase
   * session. The gateway session key is handed over in the URL fragment —
   * never sent to a server — and the mall signs in with it, so the shopper
   * does not have to log in a second time.
   */
  private _openMall() {
    const url = new URL(mallBaseUrl(), window.location.origin);
    if (this.sessionKey) {
      url.hash = new URLSearchParams({ sso: this.sessionKey, email: this.email }).toString();
    }
    window.location.assign(url.toString());
  }

  /** Activate an outlet context — Home (outlet node) or one of its subtree panels. */
  private _selectOutletPanel(db: OwnedOutlet, panel: 'home' | 'stocks' | 'sales' | 'wholesales' | 'purchases' | 'members' | 'customers' | 'suppliers' | 'salesrep' | 'promoter' | 'report' | 'mallorders' | 'accounting' | 'chartofaccount') {
    const changed = this._selectedDB?.code !== db.code;
    this._selectedDB = db;
    if (db.renewalOnly) {
      // Expired outlet: only the renewal panel is available. It has no
      // ppServer session, so do not load the banner/home data.
      this._panel = 'home';
      this._expandedOutlets = { ...this._expandedOutlets, [db.code]: true };
      this._rootExpanded = true;
      this._sidebarOpen = false;
      return;
    }
    if (changed) {
      this._loadBanner();
      this._loadTheme();
    }
    this._panel = panel;
    this._expandedOutlets = { ...this._expandedOutlets, [db.code]: true };
    this._rootExpanded = true;
    // Auto-close sidebar on mobile after selection
    this._sidebarOpen = false;
  }

  /** Select the "My Stores" root — combined financial dashboard. */
  private _selectRoot() {
    // Users without the role keep the tree usable (Find item / outlets) but
    // never open a dashboard they cannot load.
    if (!this._dashboardOutlets.length) {
      this._rootExpanded = true;
      this._sidebarOpen = false;
      return;
    }
    this._clearStoreSelection();
    this._panel = 'portfolio';
    this._rootExpanded = true;
    this._sidebarOpen = false;
  }

  /** Root-level "Find item" entry — the federated stock search. */
  private _selectFindItem() {
    this._clearStoreSelection();
    this._panel = 'stocksearch';
    this._rootExpanded = true;
    this._sidebarOpen = false;
  }

  /** Portfolio row click — open that outlet's dashboard/tools panel. */
  private _openPortfolioOutlet(e: Event) {
    const code = (e as CustomEvent<{ code?: string }>).detail?.code || '';
    const outlet = this._registeredOutlets.find((db) => db.code === code);
    if (outlet) this._selectOutletPanel(outlet, 'home');
  }

  /** Global subscription/billing page (works even with zero visible outlets). */
  private _selectBilling(focusOutlet = '') {
    this._clearStoreSelection();
    this._billingFocus = focusOutlet;
    this._panel = 'billing';
    this._sidebarOpen = false;
  }

  private _toggleRoot() {
    this._rootExpanded = !this._rootExpanded;
  }

  private _toggleOutlet(db: OwnedOutlet) {
    this._expandedOutlets = { ...this._expandedOutlets, [db.code]: !this._expandedOutlets[db.code] };
  }

  /** True when a subtree item is the active panel for the given outlet. */
  private _isActive(db: OwnedOutlet, panel: string): boolean {
    return this._panel === panel && this._selectedDB?.code === db.code;
  }

  private async _loadBanner() {
    // Clear immediately so the old banner doesn't linger while loading.
    this._bannerHtml = '';
    this._bannerExists = false;
    if (!this._selectedDB?.code) return;
    try {
      const result = await api.getStorePage(this._selectedDB.code, 'banner');
      this._bannerExists = result.exists;
      this._bannerHtml = result.html;
    } catch {
      // leave cleared on error
    }
  }

  private _clearStoreSelection() {
    this._selectedDB = null;
    this._bannerHtml = '';
    this._bannerExists = false;
    this._applyTheme(null);
  }

  /** Grouped subtree items shown under every registered outlet. */
  private get _treeGroups(): Array<{
    label?: string;
    items: Array<{
      id: 'stocks' | 'sales' | 'wholesales' | 'purchases' | 'members' | 'customers' | 'suppliers' | 'salesrep' | 'promoter' | 'report' | 'mallorders' | 'accounting' | 'chartofaccount';
      label: string;
      icon: string;
    }>;
  }> {
    return [
      {
        label: 'Masters',
        items: [
          { id: 'stocks', label: 'Stocks', icon: '📦' },
          { id: 'members', label: 'Members', icon: '👥' },
          { id: 'customers', label: 'Customers', icon: '🧑' },
          { id: 'suppliers', label: 'Suppliers', icon: '🏭' },
          { id: 'salesrep', label: 'Sales Rep', icon: '💼' },
          { id: 'promoter', label: 'Promoter', icon: '📣' },
        ],
      },
      {
        items: [
          { id: 'sales', label: 'Sales', icon: '🧾' },
          { id: 'wholesales', label: 'Wholesales', icon: '🏷️' },
          { id: 'purchases', label: 'Purchases', icon: '🚚' },
        ],
      },
      {
        items: [
          { id: 'mallorders', label: 'Online Mall Order', icon: '🛍️' },
        ],
      },
      {
        items: [
          { id: 'report', label: 'Report', icon: '📊' },
        ],
      },
      {
        // Rendered after a separator (group boundary) — Chart Of Account then
        // the Accounting hub.
        items: [
          { id: 'chartofaccount', label: 'Chart Of Account', icon: '📒' },
          { id: 'accounting', label: 'Accounting', icon: '🧮' },
        ],
      },
    ];
  }

  /** Stores where the user holds at least one role other than OnlineCustomer/OnlineSupplier,
   *  plus expired outlets they may renew (renewal-only rows have no ppServer roles). */
  private get _registeredOutlets() {
    return this.ownedDBs.filter((db) =>
      db.renewalOnly ||
      db.accessAs?.some((a) => a !== 'ET_OnlineCustomer' && a !== 'ET_OnlineSupplier'),
    );
  }

  /** Outlets the user may see in the financial dashboards. */
  private get _dashboardOutlets() {
    return this._registeredOutlets.filter(
      (db) => !db.renewalOnly && (db.accessAs || []).includes('ET_Dashboard'),
    );
  }

  /** Apply (or clear) theme CSS custom properties on document root. */
  private _applyTheme(theme: OutletTheme | null) {
    const root = document.documentElement;
    const fields: (keyof OutletTheme)[] = ['backgroundColor', 'surfaceColor', 'accentColor', 'textColor', 'borderColor', 'fontFamily'];
    if (theme) {
      for (const f of fields) {
        root.style.setProperty(THEME_VARS[f], theme[f]);
      }
      this._currentTheme = theme;
    } else {
      for (const f of fields) {
        root.style.removeProperty(THEME_VARS[f]);
      }
      this._currentTheme = null;
    }
  }

  private async _loadTheme() {
    if (!this._selectedDB?.code) {
      this._applyTheme(null);
      return;
    }
    try {
      const result = await api.getStoreTheme(this._selectedDB.code);
      this._applyTheme(result.exists && result.theme ? result.theme : null);
    } catch {
      // theme fetch failed silently — keep current look
    }
  }

  private _openThemeModal() {
    const theme = this._currentTheme || DEFAULT_THEME;
    this._themeDraft = { ...theme };
    this._themeError = '';
    this._themeModalOpen = true;
  }

  private _closeThemeModal() {
    this._themeModalOpen = false;
    this._themeDraft = { ...(this._currentTheme || DEFAULT_THEME) };
    this._themeError = '';
  }

  private async _saveTheme() {
    if (!this._selectedDB?.code) return;
    this._themeSaving = true;
    this._themeError = '';
    try {
      const result = await api.setStoreTheme(this._selectedDB.code, this._themeDraft);
      this._applyTheme(this._themeDraft);
      this._themeModalOpen = false;
      console.log('[theme] Saved by', result.data.updatedBy, 'at', new Date(result.data.dateUpdated).toISOString());
    } catch (err: any) {
      this._themeError = err.message || 'Failed to save theme.';
    } finally {
      this._themeSaving = false;
    }
  }

  private _triggerBannerUpload() {
    const input = this.shadowRoot?.getElementById('banner-upload-input') as HTMLInputElement;
    input?.click();
  }

  private async _onBannerFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this._selectedDB?.code) return;

    this._bannerUploading = true;
    try {
      await api.uploadStorePage(this._selectedDB.code, 'banner', file);
      await this._loadBanner();
    } catch (err: any) {
      console.error('[banner] Upload error:', err);
    } finally {
      this._bannerUploading = false;
    }
  }

  // ── FCM & lifecycle ──

  /** Notification deep link (`?store=&panel=&id=`) waiting for the outlet list. */
  private _deepLink: { store: string; panel: string; id: string; focus: string } | null = null;
  private _deepLinkApplied = false;

  /** Panel a push notification opens (FCM data.type → sidebar panel). */
  private static readonly PUSH_PANELS: Record<string, string> = {
    low_stock: 'stocks',
    below_cost_sale: 'sales',
    backdated_cash_sale: 'sales',
    mall_order: 'mallorders',
    mall_order_paid: 'mallorders',
  };

  /** Panels a deep link may open. */
  private static readonly DEEP_LINK_PANELS = new Set([
    'home', 'portfolio', 'stocks', 'sales', 'wholesales', 'purchases', 'members', 'customers',
    'suppliers', 'salesrep', 'promoter', 'report', 'mallorders', 'accounting', 'chartofaccount',
    'billing',
  ]);

  /** Open the outlet + panel a push notification is about. */
  private _openPushTarget(data?: Record<string, string> | null) {
    if (!data) return;
    const type = data.type || '';
    if (type === 'subscription_expiry' || type === 'subscription_renewed') {
      // Prefer the outlet-level subscription panel; fall back to the global
      // billing page when the outlet is not in the current store list.
      const outlet = this._registeredOutlets.find((db) => db.code === (data.storeCode || ''));
      if (outlet) {
        this._billingRequest = outlet.code;
        this._selectOutletPanel(outlet, 'home');
      } else {
        this._selectBilling(data.storeCode || '');
      }
      return;
    }
    const panel = AppShell.PUSH_PANELS[type];
    const code = data.storeCode || '';
    if (!panel || !code) return;
    const outlet = this._registeredOutlets.find((db) => db.code === code);
    if (!outlet) return;
    this._selectOutletPanel(outlet, panel as any);
  }

  /** Read the notification deep link (`?store=CODE&panel=PANEL[&id=ID]`). */
  private _readDeepLink() {
    try {
      const params = new URLSearchParams(window.location.search);
      const store = params.get('store') || '';
      const returningFromPayment = (params.get('payment') || '').startsWith('return') || !!params.get('order_id');
      const panelParam = params.get('panel') || (returningFromPayment ? 'billing' : 'home');
      if (!store && panelParam !== 'billing' && panelParam !== 'portfolio') return;
      this._deepLink = {
        store,
        panel: AppShell.DEEP_LINK_PANELS.has(panelParam) ? panelParam : 'home',
        id: params.get('id') || '',
        focus: params.get('outlet') || '',
      };
    } catch {
      /* malformed URL — ignore */
    }
  }

  /** Apply the deep link once the outlet list is known. */
  private _applyDeepLink() {
    const link = this._deepLink;
    if (!link || this._deepLinkApplied) return;
    if (link.panel === 'billing') {
      const outlet = this._registeredOutlets.find((db) => db.code === (link.focus || link.store));
      if (outlet) {
        this._billingRequest = outlet.code;
        this._selectOutletPanel(outlet, 'home');
      } else {
        this._selectBilling(link.focus || link.store);
      }
      this._deepLinkApplied = true;
      return;
    }
    if (link.panel === 'portfolio') {
      this._selectRoot();
      this._deepLinkApplied = true;
      return;
    }
    const outlet = this._registeredOutlets.find((db) => db.code === link.store);
    if (!outlet) return;
    this._selectOutletPanel(outlet, link.panel as any);
    this._deepLinkApplied = true;
  }

  protected updated(changed: PropertyValues) {
    if (changed.has('ownedDBs')) this._applyDeepLink();
  }

  protected firstUpdated() {
    this._readDeepLink();
    this._applyDeepLink();
    this._initFCM();
    this._fetchApprovals();
    this._loadPublicStores();
    this._initServerEvents();
    this._initTradeListInvalidation();
    this._lifecycleUnsub = serverEvents.onLifecycle((ev) => this._onServerLifecycle(ev));
    // Expiry is computed from absolute timestamps; ticking once a minute is
    // enough to flip the colours at midnight and keeps "in N days" honest.
    this._expiryTimer = window.setInterval(() => { this._expiryTick++; }, 60_000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Shell removed (logout) — stop the live-event socket + lifecycle feed.
    this._lifecycleUnsub?.();
    this._lifecycleUnsub = null;
    this._stopTradeListInvalidation();
    if (this._storesRefreshTimer !== null) {
      window.clearTimeout(this._storesRefreshTimer);
      this._storesRefreshTimer = null;
    }
    if (this._expiryTimer !== null) {
      window.clearInterval(this._expiryTimer);
      this._expiryTimer = null;
    }
    serverEvents.disconnect();

    // Logout: stop push notifications for this account on this device. The
    // token itself stays valid, so leaving it registered would keep delivering
    // the previous user's notifications to a shared device.
    const fcmToken = getCurrentFcmToken();
    if (fcmToken) {
      void api.unregisterDevice(fcmToken).catch(() => undefined);
    }
  }

  // ── Live server events (auto-refresh) ──

  /**
   * Connect the WebSocket event bus and subscribe to every registered outlet.
   * Events let open panels refresh when a record they display changes on
   * ppServer (same mechanism as the desktop client's ServerEventMapping).
   */
  private _initServerEvents() {
    if (!this.sessionKey) return;
    serverEvents.connect(this.sessionKey);
    const codes = this._registeredOutlets.map((db) => db.code);
    if (codes.length) serverEvents.subscribe(codes);
  }

  /**
   * Trade lists (Receipt / On Hold Receipt / Wholesales Invoices / Receive
   * Stock List and their hold variants) unmount when their tab is not shown,
   * so they cannot hear server events while closed. The shell stays mounted:
   * invalidate the kept list snapshot for the affected outlet so the next
   * visit refetches instead of restoring stale rows — the same rows the
   * desktop list windows reload on these events.
   */
  private _tradeEventUnsubs: Array<() => void> = [];

  private _initTradeListInvalidation() {
    if (this._tradeEventUnsubs.length) return;
    for (const typeName of Object.keys(TRADE_LIST_VIEWS)) {
      this._tradeEventUnsubs.push(
        serverEvents.on(typeName, (ev: ServerEvent) => {
          if (!ev.store || ev.typeName !== typeName) return;
          if (!LIST_CHANGE_CODES.has(ev.code)) return;
          const target = TRADE_LIST_VIEWS[typeName];
          if (target) invalidateOutletView(target.scope, target.key(ev.store));
        }),
      );
    }
    // A reconnect may have missed events entirely — drop every outlet's lists.
    this._tradeEventUnsubs.push(
      serverEvents.onLifecycle((ev: LifecycleEvent) => {
        if (ev.event !== 'resync') return;
        for (const db of this._registeredOutlets) this._invalidateTradeLists(db.code);
      }),
    );
  }

  private _invalidateTradeLists(store: string) {
    if (!store) return;
    for (const target of Object.values(TRADE_LIST_VIEWS)) {
      invalidateOutletView(target.scope, target.key(store));
    }
  }

  private _stopTradeListInvalidation() {
    this._tradeEventUnsubs.forEach((fn) => fn());
    this._tradeEventUnsubs = [];
  }

  private _lifecycleUnsub: (() => void) | null = null;
  private _storesRefreshTimer: number | null = null;
  private _expiryTimer: number | null = null;

  /** True while the outlet's tunnel server is disconnected (per lifecycle events). */
  private _isServerOffline(serverId: string): boolean {
    return !!serverId && !!this._offlineServers[serverId];
  }

  /** Live availability of one of the user's outlets. */
  private _isOutletOnline(db: OwnedOutlet): boolean {
    return !this._isServerOffline(db.hostAddress);
  }

  // ── Subscription expiry presentation ────────────────────────────────
  // DB hosting (availabledb.expiry) and the gateway subscription are separate
  // values and are presented separately: the outlet tint follows DB hosting,
  // the GW pill/banner follows the gateway subscription.

  /** DB-hosting state, recomputed locally from the absolute expiry. */
  private _dbState(db: OwnedOutlet): SubscriptionState {
    void this._expiryTick; // re-run on the 60 s tick
    const sub = db.subscription?.db;
    // Without detail (older cached payload) the generic renewal-only banner is
    // used instead of guessing which subscription lapsed.
    if (!sub) return { status: 'unknown', daysLeft: 0 };
    return subscriptionState(sub.expiresAt, { enforced: sub.enforced, forever: sub.forever });
  }

  private _gatewayState(db: OwnedOutlet): SubscriptionState {
    void this._expiryTick;
    const sub = db.subscription?.gateway;
    if (!sub) return { status: 'unknown', daysLeft: 0 };
    return subscriptionState(sub.expiresAt, { enforced: true, forever: false });
  }

  /** Amber/red class for an outlet row and the right panel. */
  private _accentClass(db: OwnedOutlet | null): '' | 'expiring' | 'expired' {
    if (!db) return '';
    if (db.renewalOnly) return 'expired';
    const state = this._dbState(db);
    if (state.status === 'expired') return 'expired';
    if (state.status === 'expiring') return 'expiring';
    return '';
  }

  private _dbStatusText(db: OwnedOutlet): string {
    if (db.renewalOnly) return 'Expired';
    const state = this._dbState(db);
    if (state.status === 'expired') return 'Expired';
    if (state.status === 'expiring') return `Expiring ${state.daysLeft}d`;
    return this._isOutletOnline(db) ? 'Online' : 'Offline';
  }

  private _pillClass(db: OwnedOutlet): string {
    if (db.renewalOnly || this._dbState(db).status === 'expired') return 'expired';
    if (this._dbState(db).status === 'expiring') return 'expiring';
    return this._isOutletOnline(db) ? 'online' : 'offline';
  }

  /** Independent gateway-subscription badge (only when it needs attention). */
  private _gatewayBadge(db: OwnedOutlet) {
    const state = this._gatewayState(db);
    if (state.status === 'expired') {
      return html`<span class="gw-pill expired" title="Gateway subscription expired">GW expired</span>`;
    }
    if (state.status === 'expiring') {
      return html`<span class="gw-pill expiring" title="Gateway subscription expiring">GW ${state.daysLeft}d</span>`;
    }
    return nothing;
  }

  /** Open the outlet's subscription panel (from a banner or badge). */
  private _openSubscription(db: OwnedOutlet) {
    this._billingRequest = db.code;
    this._selectOutletPanel(db, 'home');
  }

  /** Independent banners shown above the right-panel content. */
  private _renderExpiryBanners() {
    const db = this._selectedDB;
    if (!db) return nothing;
    const reasons = db.expiredReasons || [];
    const dbState = this._dbState(db);
    const gwState = this._gatewayState(db);
    // rejected outlets may carry only the reason list (no subscription block).
    const dbExpired = dbState.status === 'expired' || reasons.includes('database');
    const gwExpired = gwState.status === 'expired' || reasons.includes('gateway');
    const parts = [];

    if (dbExpired) {
      const when = db.subscription?.db?.expiresAt
        ? ` on ${formatExpiryDateMs(db.subscription.db.expiresAt)}`
        : '';
      parts.push(html`
        <div class="expiry-banner expired">
          <span>🔒</span>
          <span>Database hosting expired${when}. Normal operations are disabled — renew to continue.</span>
          <button class="expiry-action" @click=${() => this._openSubscription(db)}>Renew</button>
        </div>`);
    } else if (dbState.status === 'expiring') {
      const when = db.subscription?.db?.expiresAt
        ? ` (${formatExpiryDateMs(db.subscription.db.expiresAt)})`
        : '';
      parts.push(html`
        <div class="expiry-banner expiring">
          <span>⏳</span>
          <span>Database hosting expires in ${dbState.daysLeft} day(s)${when}.</span>
          <button class="expiry-action" @click=${() => this._openSubscription(db)}>Renew</button>
        </div>`);
    }

    if (gwExpired) {
      parts.push(html`
        <div class="expiry-banner expired">
          <span>🌐</span>
          <span>Gateway subscription expired — online/mall access is blocked until it is renewed.</span>
          <button class="expiry-action" @click=${() => this._openSubscription(db)}>Renew</button>
        </div>`);
    } else if (gwState.status === 'expiring') {
      parts.push(html`
        <div class="expiry-banner expiring">
          <span>🌐</span>
          <span>Gateway subscription expires in ${gwState.daysLeft} day(s).</span>
          <button class="expiry-action" @click=${() => this._openSubscription(db)}>Renew</button>
        </div>`);
    }

    // Renewal-only outlet with no detailed state (older cached payload).
    if (!dbExpired && !gwExpired && db.renewalOnly) {
      parts.push(html`
        <div class="expiry-banner expired">
          <span>🔒</span>
          <span>This outlet's subscription has expired — renew to continue.</span>
          <button class="expiry-action" @click=${() => this._openSubscription(db)}>Renew</button>
        </div>`);
    }
    return parts;
  }

  /**
   * Gateway lifecycle hints drive the outlet availability indicators:
   *  - server_offline ⇒ badge the outlet offline immediately.
   *  - server_online / resync(serverId) ⇒ badge it online and re-resolve the
   *    store list, so outlets that were offline at login reappear without
   *    logging out and back in.
   *  - resync (no serverId) ⇒ the WS itself reconnected; availability may
   *    have changed while we were away.
   */
  private _onServerLifecycle(ev: LifecycleEvent) {
    if (ev.event === 'server_offline' && ev.serverId) {
      this._offlineServers = { ...this._offlineServers, [ev.serverId]: true };
      return;
    }
    if ((ev.event === 'server_online' || ev.event === 'resync') && ev.serverId) {
      if (this._offlineServers[ev.serverId]) {
        const next = { ...this._offlineServers };
        delete next[ev.serverId];
        this._offlineServers = next;
      }
      this._refreshStoresSoon();
      return;
    }
    if (ev.event === 'resync') {
      // Socket (re)connected — availability may have changed while away.
      // Run immediately (no debounce): this is also the first connect after a
      // page load, where the restored sessionStorage snapshot may be stale.
      void this._refreshStores();
    }
  }

  private _refreshStoresSoon() {
    if (this._storesRefreshTimer !== null) return;
    this._storesRefreshTimer = window.setTimeout(() => {
      this._storesRefreshTimer = null;
      void this._refreshStores();
    }, 1200);
  }

  /**
   * Re-resolve the user's stores (silent re-login). Freshly returned entries
   * win; entries missing from the fresh result are kept ONLY while their
   * server is known to be offline (so offline outlets remain visible with
   * their badge instead of disappearing).
   */
  private async _refreshStores() {
    if (!this.sessionKey) return;
    let idToken = '';
    try {
      idToken = (await getIdToken()) || '';
    } catch {
      return;
    }
    if (!idToken) return;
    try {
      const result = await api.refreshStores(idToken);
      const fresh = result?.data?.ownedDBs;
      if (!Array.isArray(fresh)) return;
      // An EMPTY result is meaningful now: outlets that lapsed are returned as
      // renewalOnly rows, so an empty list genuinely means "nothing available".
      // Only rows whose server is known offline survive (their absence from
      // the fresh list is just the disconnected tunnel, not expiry).
      const freshCodes = new Set(fresh.map((db) => db.code));
      const kept = this.ownedDBs.filter(
        (db) => !freshCodes.has(db.code) && this._isServerOffline(db.hostAddress),
      );
      const merged = [...fresh, ...kept].sort((a, b) =>
        (a.name || a.code).localeCompare(b.name || b.code),
      );
      this.ownedDBs = merged;
      const selected = this._selectedDB
        ? merged.find((db) => db.code === this._selectedDB!.code)
        : undefined;
      if (selected) {
        // Always adopt the fresh entry: expiry/status blocks change without the
        // renewalOnly flag flipping (e.g. a new expiry date after a renewal).
        const wasRenewalOnly = !!this._selectedDB!.renewalOnly;
        this._selectedDB = selected;
        if (wasRenewalOnly && !selected.renewalOnly) {
          this._loadBanner();
          this._loadTheme();
        }
      }
      this.dispatchEvent(
        new CustomEvent('owned-dbs-changed', {
          detail: { ownedDBs: merged },
          bubbles: true,
          composed: true,
        }),
      );
      // Newly-served outlets need live data-event subscriptions too.
      const codes = this._registeredOutlets.map((db) => db.code);
      if (codes.length) serverEvents.subscribe(codes);
    } catch {
      /* transient — the next lifecycle event retries */
    }
  }

  private async _initFCM() {
    try {
      // Browsers (iOS Safari in particular) require a user gesture before
      // showing the notification permission prompt, so don't ask on page load.
      // When permission is already granted, register immediately; otherwise
      // try once on the first user interaction.
      if ('Notification' in window && Notification.permission === 'default') {
        const onFirstGesture = () => {
          window.removeEventListener('pointerdown', onFirstGesture, true);
          void this._registerForPush();
        };
        window.addEventListener('pointerdown', onFirstGesture, true);
      } else {
        await this._registerForPush();
      }

      // Handle token refresh
      onFcmTokenRefresh(async (newToken) => {
        try { await api.registerDevice(newToken); } catch { /* ignore */ }
      });

      // Handle foreground messages
      onForegroundMessage((payload) => {
        // Refresh approvals and jump to the outlet/panel the push is about.
        this._fetchApprovals();
        this._openPushTarget((payload?.data as Record<string, string> | undefined) ?? null);
        // FCM suppresses the system notification while the app is focused, so
        // show it ourselves — otherwise a push that lands while the user is
        // looking at the app is completely invisible.
        void this._showForegroundNotification(payload);
      });

      // A notification click focuses the app and posts its payload.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
          if (ev.data?.type === 'ppos-notification-click') {
            void this._fetchApprovals();
            this._openPushTarget(ev.data?.data ?? null);
          }
        });
      }
    } catch {
      // FCM optional — app works without push notifications
    }
  }

  private async _registerForPush() {
    try {
      const fcmToken = await registerFCM();
      if (fcmToken) {
        try { await api.registerDevice(fcmToken); } catch { /* ignore */ }
      }
    } catch {
      // FCM optional — the app works without push notifications
    }
  }

  /**
   * Show a push that arrived while the app was focused.
   *
   * The Firebase JS SDK only displays `notification` payloads when the page is
   * hidden; in the foreground it delivers them to onMessage and renders
   * nothing. Without this, an event that happens while the user is in the app
   * (e.g. a mall order placed on another tab/device) is silent. Rendering it
   * through the FCM service worker keeps the existing notificationclick
   * handler (focus the app + deep link) working for it too.
   */
  private async _showForegroundNotification(payload: any) {
    try {
      const note = payload?.notification;
      if (!note?.title) return; // data-only message: nothing to display
      const data = (payload?.data || {}) as Record<string, string>;
      const registration =
        (await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope')) ||
        (await navigator.serviceWorker.getRegistration('/'));
      if (!registration) return;
      await registration.showNotification(note.title, {
        body: note.body || '',
        // Same event → replace; different events → stack. `kind` keeps a
        // DB-hosting renewal and a gateway renewal apart when the server does
        // not supply a per-event refId.
        tag: `${data.type || 'ppos'}:${data.refId || data.orderId || data.kind || ''}`,
        data: { ...data, url: data.url || '/' },
      });
    } catch {
      // Never let a display failure break the handler
    }
  }

  /** Bell button: register this device (if needed) and send a test push. */
  private async _testNotifications() {
    try {
      if (typeof Notification === 'undefined') {
        window.alert('This browser does not support notifications.');
        return;
      }
      if (Notification.permission === 'denied') {
        window.alert(
          'Notifications are blocked for this site. Allow them in the browser settings, then reload the page.',
        );
        return;
      }
      const token = await registerFCM();
      if (!token) {
        window.alert(
          `Could not obtain a push token: ${getLastFcmError() || 'unknown reason'}.`,
        );
        return;
      }
      try { await api.registerDevice(token); } catch { /* the test call reports the real state */ }
      const result = await api.testNotification();
      window.alert(
        `Test notification queued to ${result.data.devices} device(s). If nothing appears, check the OS notification settings for this browser.`,
      );
    } catch (err: any) {
      window.alert(err?.message || 'Could not send a test notification.');
    }
  }

  private async _fetchApprovals() {
    try {
      const result = await api.getPendingApprovals();
      if (result.success) {
        this._approvalsAsOwner = result.data.asOwner;
        this._approvalsAsRequester = result.data.asRequester;
      }
    } catch {
      // Silently fail — approvals are non-critical
    }
  }

  /** Load the list of publicly accessible stores for the "All Stores" dropdown group. */
  private async _loadPublicStores() {
    try {
      const result = await api.getAvailableStores();
      if (result.success) {
        this._publicStores = result.data;
      }
    } catch {
      // Silently fail — the dropdown just won't show the public store list.
    }
  }

  private get _pendingOwnerApprovalsCount(): number {
    return this._approvalsAsOwner.filter((a) => a.status === 'pending').length;
  }

  private async _openApprovalsModal() {
    this._approvalsModalOpen = true;
    this._approvalsActionError = '';
    await this._fetchApprovals();
  }

  private _closeApprovalsModal() {
    this._approvalsModalOpen = false;
    this._approvalsActionError = '';
  }

  private async _handleApprove(requestId: string) {
    this._approvalsLoading = true;
    this._approvalsActionError = '';
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        this._approvalsActionError = 'Authentication expired.';
        this._approvalsLoading = false;
        return;
      }
      await api.approveRegistration(requestId, idToken);
      await this._fetchApprovals();
    } catch (err: any) {
      this._approvalsActionError = err.message || 'Approval failed';
    } finally {
      this._approvalsLoading = false;
    }
  }

  private async _handleReject(requestId: string) {
    this._approvalsLoading = true;
    this._approvalsActionError = '';
    try {
      await api.rejectRegistration(requestId);
      await this._fetchApprovals();
    } catch (err: any) {
      this._approvalsActionError = err.message || 'Rejection failed';
    } finally {
      this._approvalsLoading = false;
    }
  }

  private async _openRegisterModal(preselectCode?: string) {
    this._regModalOpen = true;
    this._regError = '';
    this._regSuccess = '';
    this._regSelectedStore = preselectCode || '';
    this._regSelectedRole = '';
    this._regLoadingStores = true;

    try {
      const result = await api.getAvailableStores();
      if (result.success) {
        this._regStores = result.data;
      }
    } catch (err: any) {
      this._regError = err.message || 'Failed to load available stores';
    } finally {
      this._regLoadingStores = false;
    }
  }

  private _closeRegisterModal() {
    this._regModalOpen = false;
    this._regStores = [];
    this._regSelectedStore = '';
    this._regSelectedRole = '';
    this._regError = '';
    this._regSuccess = '';
  }

  private _selectRegStore(code: string) {
    this._regSelectedStore = this._regSelectedStore === code ? '' : code;
    this._regError = '';
    this._regSuccess = '';
  }

  private _selectRegRole(role: string) {
    this._regSelectedRole = this._regSelectedRole === role ? '' : role;
    this._regError = '';
    this._regSuccess = '';
  }

  private async _submitRegistration() {
    if (!this._regSelectedStore || !this._regSelectedRole) {
      this._regError = 'Please select both a store and a role.';
      return;
    }

    const store = this._regStores.find((s) => s.code === this._regSelectedStore);
    if (!store) {
      this._regError = 'Selected store not found.';
      return;
    }

    this._regLoading = true;
    this._regError = '';
    this._regSuccess = '';
    this._regNeedsApproval = false;

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        this._regError = 'Authentication expired. Please login again.';
        this._regLoading = false;
        return;
      }

      const result = await api.registerStore(
        idToken,
        store.hostAddress,
        store.hostPort,
        this._regSelectedRole,
      );

      if (result.success) {
        this._regSuccess = result.data.message;

        if (result.data.needsApproval) {
          // Registration requires owner approval — don't update ownedDBs yet
          this._regNeedsApproval = true;
        } else if (result.data.ownedDBs) {
          // Auto-approved — update immediately
          this.ownedDBs = result.data.ownedDBs;
          const saved = sessionStorage.getItem('ppos_session');
          if (saved) {
            try {
              const data = JSON.parse(saved);
              data.ownedDBs = result.data.ownedDBs;
              sessionStorage.setItem('ppos_session', JSON.stringify(data));
            } catch { /* ignore */ }
          }
          // Refresh the public store list — the newly registered store should be filtered out.
          this._loadPublicStores();
          // Close modal after a short delay
          setTimeout(() => this._closeRegisterModal(), 1500);
        }
      }
    } catch (err: any) {
      this._regError = err.message || 'Registration failed. Please try again.';
    } finally {
      this._regLoading = false;
    }
  }

  /** Available roles for store registration */
  private get _registrationRoles(): Array<{ value: string; label: string }> {
    return [
      { value: 'ET_User', label: 'User' },
      { value: 'ET_Customer', label: 'Customer' },
      { value: 'ET_Member', label: 'Member' },
      { value: 'ET_Supplier', label: 'Supplier' },
      { value: 'ET_SalesRep', label: 'Sales Rep' },
      { value: 'ET_SalesPromoter', label: 'Promoter' },
      { value: 'ET_OnlineCustomer', label: 'Online Customer' },
      { value: 'ET_OnlineSupplier', label: 'Online Supplier' },
    ];
  }

  render() {
    return html`
      <!-- overlay backdrop (mobile only) -->
      <div class="overlay ${this._sidebarOpen ? 'open' : ''}" @click=${this._closeSidebar}></div>

      <header>
        <div class="header-left">
          <button class="btn-menu" @click=${this._toggleSidebar} title="Menu" aria-label="Toggle sidebar">☰</button>
        </div>
        <div class="header-center">
          <button
            class="banner-upload-btn"
            title="Test push notifications"
            aria-label="Test push notifications"
            @click=${this._testNotifications}
          >🔔</button>
          ${this._selectedDB?.accessAs?.includes('ET_Admin') ? html`
            <input
              id="banner-upload-input"
              type="file"
              accept=".html,.htm,text/html"
              style="display:none"
              @change=${this._onBannerFileSelected}
            >
            <button
              class="banner-upload-btn"
              title="Upload banner"
              ?disabled=${this._bannerUploading}
              @click=${this._triggerBannerUpload}
            >${this._bannerUploading ? '⏳' : '🖼️'}</button>
            <button
              class="banner-upload-btn"
              title="Outlet theme settings"
              @click=${this._openThemeModal}
            >⚙️</button>
          ` : ''}
          ${this._bannerExists && this._bannerHtml
            ? html`<iframe
                class="header-banner-frame"
                srcdoc=${this._bannerHtml}
                sandbox="allow-popups"
                title="Store banner"
              ></iframe>`
            : this._selectedDB
              ? html`
                  <span class="header-outlet">
                    <span class="ho-name">${this._selectedDB.name}</span>
                    <span class="status-pill ${this._pillClass(this._selectedDB)}">
                      <span class="dot"></span>${this._selectedDB.renewalOnly ? 'Expired' : this._dbStatusText(this._selectedDB)}
                    </span>
                  </span>`
              : html`<span class="outlet-none">My Stores</span>`}
        </div>
      </header>

      <div class="body">
        <!-- ── Left Sidebar: Stores ── -->
        <aside class="sidebar ${this._sidebarOpen ? 'open' : ''}">
          <div class="sidebar-header">
            <span>🗄️ Stores</span>
            <div style="display:flex;align-items:center;gap:0.5rem">
              <span>${this.ownedDBs.length}</span>
              <button class="btn-close-sidebar" @click=${this._closeSidebar} title="Close sidebar" aria-label="Close sidebar">&times;</button>
            </div>
          </div>
          <div class="sidebar-list">
            ${this._registeredOutlets.length > 0 ? html`
              <div class="tree">
                <div
                  class="tree-node tree-root ${this._panel === 'portfolio' ? 'selected' : ''}"
                  data-testid="stores-root"
                  title="Financial health of all outlets"
                  @click=${this._selectRoot}
                >
                  ${this._chevron(this._rootExpanded, (e: Event) => { e.stopPropagation(); this._toggleRoot(); })}
                  <span class="tree-icon">📊</span>
                  <span class="tree-label">My Stores</span>
                </div>
                ${this._rootExpanded ? html`
                  <button
                    class="tree-node tree-child ${this._panel === 'stocksearch' ? 'selected' : ''}"
                    data-testid="tree-root-find-item"
                    data-panel="stocksearch"
                    style="margin-left:0.75rem;width:calc(100% - 0.75rem);margin-bottom:0.35rem"
                    title="Search stock across my registered stores"
                    @click=${this._selectFindItem}
                  >
                    <span class="tree-icon">🔎</span>
                    <span class="tree-label">Find item</span>
                  </button>
                ` : ''}
                ${this._rootExpanded ? this._registeredOutlets.map((db) => {
                  const online = this._isOutletOnline(db);
                  const expired = !!db.renewalOnly;
                  const accent = this._accentClass(db);
                  const dbState = this._dbState(db);
                  return html`
                    <div
                      class="store-item ${this._isActive(db, 'home') ? 'selected' : ''} ${accent} ${online ? '' : 'offline'}"
                      data-testid="store-item"
                      data-outlet-code=${db.code}
                      title=${expired || dbState.status === 'expired'
                        ? `${db.name} — subscription expired (renew to continue)`
                        : dbState.status === 'expiring'
                          ? `${db.name} — database hosting expires in ${dbState.daysLeft} day(s)`
                          : online
                            ? `${db.name} — online`
                            : `${db.name} — offline (no live connection to this outlet)`}
                      @click=${() => this._selectOutletPanel(db, 'home')}
                    >
                      <span class="store-avatar">${this._initials(db.name)}</span>
                      <span class="store-info">
                        <span class="store-name">${db.name}</span>
                        <span class="store-sub">${db.outletName || db.hostName || db.code}</span>
                      </span>
                      <span class="status-pill ${this._pillClass(db)}"><span class="dot"></span>${this._dbStatusText(db)}</span>
                      ${this._gatewayBadge(db)}
                      ${this._chevron(!!this._expandedOutlets[db.code], (e: Event) => { e.stopPropagation(); this._toggleOutlet(db); })}
                    </div>
                    ${this._expandedOutlets[db.code] ? html`
                      <div class="tree-children">
                        ${expired ? html`
                          <button class="tree-node tree-child" data-testid="tree-item" data-panel="home"
                            @click=${() => this._selectOutletPanel(db, 'home')}>
                            <span class="tree-icon">💳</span>
                            <span class="tree-label">Subscription</span>
                          </button>
                        ` : this._treeGroups.map((group, gi) => html`
                          ${gi > 0 ? html`<div class="tree-sep" role="separator"></div>` : ''}
                          ${group.label ? html`<div class="tree-group-label">${group.label}</div>` : ''}
                          ${group.items.map((item) => html`
                            <button
                              class="tree-node tree-child ${this._isActive(db, item.id) ? 'selected' : ''}"
                              data-testid="tree-item"
                              data-panel=${item.id}
                              @click=${() => this._selectOutletPanel(db, item.id)}
                            >
                              <span class="tree-icon">${item.icon}</span>
                              <span class="tree-label">${item.label}</span>
                            </button>
                          `)}
                        `)}
                      </div>` : ''}`;
                }) : ''}
              </div>
            ` : html`
              <div class="sidebar-empty">No registered outlets.</div>
            `}
            ${this._pendingOwnerApprovalsCount > 0 ? html`
              <button class="sidebar-register-btn" style="border-color:var(--color-warning, #f59e0b);color:var(--color-warning, #f59e0b)"
                @click=${this._openApprovalsModal}>
                ⏳ Pending Approvals (${this._pendingOwnerApprovalsCount})
              </button>
            ` : ''}
            ${this._approvalsAsRequester.some((a) => a.status === 'pending') ? html`
              <button class="sidebar-register-btn" style="border-color:var(--color-text-secondary);color:var(--color-text-secondary)"
                @click=${this._openApprovalsModal}>
                📋 My Requests
              </button>
            ` : ''}
          </div>
          <div class="sidebar-footer">
            <div class="sf-user">
              <span class="sf-avatar" aria-hidden="true">👤</span>
              <span class="footer-email" title=${this.email}>${this.email}</span>
            </div>
            <button class="btn-logout" @click=${this._handleLogout} title="Logout" aria-label="Logout">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Logout
            </button>
          </div>
        </aside>

        <!-- ── Right Panel: Content ── -->
        <div class="right-panel ${this._accentClass(this._selectedDB)}">
          <main>
            ${this._renderExpiryBanners()}
            ${this._selectedDB && !this._isOutletOnline(this._selectedDB) ? html`
              <div class="offline-banner">
                ⚠️ This outlet is offline — no live connection to the gateway. Data may be stale or unavailable.
              </div>
            ` : ''}
            ${this._panel === 'portfolio' ? html`
              <stores-dashboard
                .stores=${this.ownedDBs}
                @open-outlet=${this._openPortfolioOutlet}
              ></stores-dashboard>
            ` : ''}
            ${this._panel === 'stocksearch' ? html`
              <stock-search
                .sessionKey=${this.sessionKey}
                .masterDB=${''}
                .transDB=${''}
                .registeredOnly=${true}
              ></stock-search>
            ` : ''}
            ${this._panel === 'home' && this._selectedDB ? html`
              <outlet-panel
                .storeCode=${this._selectedDB.code}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
                .canDashboard=${this._selectedDB.accessAs?.includes('ET_Dashboard') ?? false}
                .email=${this.email}
                .renewalOnly=${this._selectedDB.renewalOnly ?? false}
                .openBilling=${this._billingRequest === this._selectedDB.code}
                @outlet-renewed=${() => this._refreshStoresSoon()}
              ></outlet-panel>
            ` : ''}
            ${this._panel === 'stocks' && this._selectedDB ? html`
              <stock-manage
                .storeCode=${this._selectedDB.code}
                .sessionKey=${this.sessionKey}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
              ></stock-manage>
            ` : ''}
            ${this._panel === 'sales' && this._selectedDB ? html`
              <sales-page .storeCode=${this._selectedDB.code}></sales-page>
            ` : ''}
            ${this._panel === 'wholesales' && this._selectedDB ? html`
              <wholesales-page .storeCode=${this._selectedDB.code}></wholesales-page>
            ` : ''}
            ${this._panel === 'purchases' && this._selectedDB ? html`
              <purchases-page .storeCode=${this._selectedDB.code}></purchases-page>
            ` : ''}
            ${this._panel === 'members' && this._selectedDB ? html`
              <member-manage
                .storeCode=${this._selectedDB.code}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
              ></member-manage>
            ` : ''}
            ${(['customers', 'suppliers', 'salesrep', 'promoter'] as const).includes(this._panel as 'customers' | 'suppliers' | 'salesrep' | 'promoter') && this._selectedDB ? html`
              <company-manage
                .storeCode=${this._selectedDB.code}
                .companyType=${this._panel === 'customers' ? 'opscustomer'
                  : this._panel === 'suppliers' ? 'opscompany'
                  : this._panel === 'salesrep' ? 'opssalesrep'
                  : 'opspromoter'}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
              ></company-manage>
            ` : ''}
            ${this._panel === 'report' && this._selectedDB ? html`
              <reports-page .storeCode=${this._selectedDB.code}></reports-page>
            ` : ''}
            ${this._panel === 'accounting' && this._selectedDB ? html`
              <accounting-page
                .storeCode=${this._selectedDB.code}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
              ></accounting-page>
            ` : ''}
            ${this._panel === 'chartofaccount' && this._selectedDB ? html`
              <chart-of-account
                .storeCode=${this._selectedDB.code}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
              ></chart-of-account>
            ` : ''}
            ${this._panel === 'mallorders' && this._selectedDB ? html`
              <mall-orders
                .storeCode=${this._selectedDB.code}
                .isAdmin=${this._selectedDB.accessAs?.includes('ET_Admin') ?? false}
              ></mall-orders>
            ` : ''}
            ${this._panel === 'billing' ? html`
              <billing-page
                .email=${this.email}
                .focusOutlet=${this._billingFocus}
                @subscription-renewed=${() => this._refreshStoresSoon()}
              ></billing-page>
            ` : ''}
            ${!this._panel ? this._renderWelcome() : ''}
          </main>
        </div>
      </div>

      <!-- ── Registration Modal ── -->
      <div class="modal-overlay ${this._regModalOpen ? 'open' : ''}" @click=${this._closeRegisterModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Register for a Store</h3>
            <button class="modal-close" @click=${this._closeRegisterModal}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._regLoadingStores ? html`
              <div style="text-align:center;padding:2rem;color:var(--color-text-secondary)">
                Loading available stores&hellip;
              </div>
            ` : this._regSuccess ? html`
              <div style="text-align:center;padding:2rem">
                <div style="font-size:2rem;margin-bottom:0.5rem">${this._regNeedsApproval ? '⏳' : '✅'}</div>
                <div class="modal-success">${this._regSuccess}</div>
                ${this._regNeedsApproval ? html`
                  <div style="font-size:0.8rem;color:var(--color-text-secondary);margin-top:0.75rem">
                    You'll be notified once the store owner reviews your request.
                  </div>
                ` : ''}
              </div>
            ` : html`
              <!-- Step 1: Select Store -->
              <div class="form-group">
                <label>Select a Store</label>
                ${this._regStores.length === 0 ? html`
                  <div style="color:var(--color-text-secondary);font-size:0.85rem;padding:0.5rem 0">
                    No additional stores available. You already have access to all stores.
                  </div>
                ` : this._regStores.map((store) => html`
                  <div
                    class="store-option ${this._regSelectedStore === store.code ? 'selected' : ''}"
                    @click=${() => this._selectRegStore(store.code)}
                  >
                    <div class="s-name">${store.name}</div>
                    <div class="s-meta">${store.code} &middot; ${store.hostName}</div>
                  </div>
                `)}
              </div>

              <!-- Step 2: Select Role (only shown when a store is selected) -->
              ${this._regSelectedStore ? html`
                <div class="form-group">
                  <label>Register As</label>
                  <div class="role-chips">
                    ${this._registrationRoles.map((role) => html`
                      <div
                        class="role-chip ${this._regSelectedRole === role.value ? 'selected' : ''}"
                        @click=${() => this._selectRegRole(role.value)}
                      >${role.label}</div>
                    `)}
                  </div>
                </div>
              ` : ''}

              ${this._regError ? html`<div class="modal-error">${this._regError}</div>` : ''}
            `}
          </div>
          ${!this._regSuccess || this._regNeedsApproval ? html`
            <div class="modal-footer">
              <button class="btn-cancel" @click=${this._closeRegisterModal}>${this._regNeedsApproval ? 'Close' : 'Cancel'}</button>
              ${!this._regNeedsApproval ? html`
                <button
                  class="btn-submit"
                  ?disabled=${this._regLoading || !this._regSelectedStore || !this._regSelectedRole}
                  @click=${this._submitRegistration}
                >${this._regLoading ? 'Registering...' : 'Register'}</button>
              ` : ''}
            </div>
          ` : ''}
        </div>
      </div>

      <!-- ── Approvals Modal ── -->
      <div class="modal-overlay ${this._approvalsModalOpen ? 'open' : ''}" @click=${this._closeApprovalsModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Registration Approvals</h3>
            <button class="modal-close" @click=${this._closeApprovalsModal}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._approvalsActionError ? html`
              <div class="modal-error" style="margin-bottom:0.75rem">${this._approvalsActionError}</div>
            ` : ''}

            <!-- As Owner: pending approvals to review -->
            ${this._approvalsAsOwner.length > 0 ? html`
              <div class="form-group">
                <label>Awaiting Your Approval</label>
                ${this._approvalsAsOwner.map((a) => html`
                  <div style="padding:0.65rem 0.75rem;border:1px solid var(--color-border);border-radius:8px;margin-bottom:0.5rem">
                    <div style="display:flex;justify-content:space-between;align-items:center">
                      <div>
                        <div style="font-size:0.85rem;font-weight:500">${a.requesterEmail}</div>
                        <div style="font-size:0.7rem;color:var(--color-text-secondary)">
                          ${a.entityType.replace('ET_', '').replace(/([a-z])([A-Z])/g, '$1 $2')}
                          ${a.storeName ? ` · ${a.storeName}` : ''}
                        </div>
                        <div style="font-size:0.65rem;color:var(--color-text-secondary);margin-top:2px">
                          ${new Date(a.createdAt).toLocaleString()}
                        </div>
                      </div>
                      ${a.status === 'pending' ? html`
                        <div style="display:flex;gap:4px">
                          <button
                            class="btn-submit" style="padding:4px 12px;font-size:0.75rem"
                            ?disabled=${this._approvalsLoading}
                            @click=${() => this._handleApprove(a.id)}
                          >✓ Approve</button>
                          <button
                            class="btn-cancel" style="padding:4px 12px;font-size:0.75rem;color:var(--color-error);border-color:var(--color-error)"
                            ?disabled=${this._approvalsLoading}
                            @click=${() => this._handleReject(a.id)}
                          >✗ Reject</button>
                        </div>
                      ` : html`
                        <span style="font-size:0.75rem;color:${a.status === 'approved' ? 'var(--color-success)' : 'var(--color-error)'}">
                          ${a.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                        </span>
                      `}
                    </div>
                  </div>
                `)}
              </div>
            ` : ''}

            <!-- As Requester: my requests -->
            ${this._approvalsAsRequester.length > 0 ? html`
              <div class="form-group">
                <label>My Requests</label>
                ${this._approvalsAsRequester.map((a) => html`
                  <div style="padding:0.5rem 0.75rem;border:1px solid var(--color-border);border-radius:8px;margin-bottom:0.35rem;font-size:0.8rem">
                    <span style="font-weight:500">
                      ${a.entityType.replace('ET_', '').replace(/([a-z])([A-Z])/g, '$1 $2')}
                    </span>
                    ${a.storeName ? html` · ${a.storeName}` : ''}
                    <span style="float:right;color:${a.status === 'approved' ? 'var(--color-success)' : a.status === 'rejected' ? 'var(--color-error)' : 'var(--color-warning, #f59e0b)'}">
                      ${a.status === 'approved' ? '✓ Approved' : a.status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
                    </span>
                    <div style="font-size:0.65rem;color:var(--color-text-secondary)">${new Date(a.createdAt).toLocaleString()}</div>
                  </div>
                `)}
              </div>
            ` : ''}

            ${this._approvalsAsOwner.length === 0 && this._approvalsAsRequester.length === 0 ? html`
              <div style="text-align:center;padding:2rem;color:var(--color-text-secondary)">
                No pending approvals or requests.
              </div>
            ` : ''}
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${this._closeApprovalsModal}>Close</button>
          </div>
        </div>
      </div>

      <!-- ── Theme Settings Modal ── -->
      <div class="modal-overlay ${this._themeModalOpen ? 'open' : ''}" @click=${this._closeThemeModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Theme Settings</h3>
            <button class="modal-close" @click=${this._closeThemeModal}>&times;</button>
          </div>
          <div class="modal-body">
            ${!this._selectedDB ? html`
              <div style="text-align:center;padding:1rem;color:var(--color-text-secondary)">
                Select a store first to manage its theme.
              </div>
            ` : !this._selectedDB.accessAs?.includes('ET_Admin') ? html`
              <div style="text-align:center;padding:1rem;color:var(--color-text-secondary)">
                Only Admin users can change the outlet theme.
                ${this._currentTheme ? html`
                  <div style="margin-top:0.5rem;font-size:0.8rem">
                    A custom theme is currently active for this outlet.
                  </div>
                ` : html`
                  <div style="margin-top:0.5rem;font-size:0.8rem">
                    This outlet is using the default look.
                  </div>
                `}
              </div>
            ` : html`
              <div class="form-group">
                <label>Background</label>
                <input type="color" .value=${this._themeDraft.backgroundColor}
                  @input=${(e: InputEvent) => this._themeDraft = { ...this._themeDraft, backgroundColor: (e.target as HTMLInputElement).value }}
                />
              </div>
              <div class="form-group">
                <label>Surface / Cards</label>
                <input type="color" .value=${this._themeDraft.surfaceColor}
                  @input=${(e: InputEvent) => this._themeDraft = { ...this._themeDraft, surfaceColor: (e.target as HTMLInputElement).value }}
                />
              </div>
              <div class="form-group">
                <label>Accent / Buttons</label>
                <input type="color" .value=${this._themeDraft.accentColor}
                  @input=${(e: InputEvent) => this._themeDraft = { ...this._themeDraft, accentColor: (e.target as HTMLInputElement).value }}
                />
              </div>
              <div class="form-group">
                <label>Text</label>
                <input type="color" .value=${this._themeDraft.textColor}
                  @input=${(e: InputEvent) => this._themeDraft = { ...this._themeDraft, textColor: (e.target as HTMLInputElement).value }}
                />
              </div>
              <div class="form-group">
                <label>Borders</label>
                <input type="color" .value=${this._themeDraft.borderColor}
                  @input=${(e: InputEvent) => this._themeDraft = { ...this._themeDraft, borderColor: (e.target as HTMLInputElement).value }}
                />
              </div>
              <div class="form-group">
                <label>Font</label>
                <select
                  @change=${(e: Event) => this._themeDraft = { ...this._themeDraft, fontFamily: (e.target as HTMLSelectElement).value }}
                >
                  ${FONT_OPTIONS.map((f) => html`
                    <option value=${f.value} ?selected=${this._themeDraft.fontFamily === f.value}>${f.label}</option>
                  `)}
                </select>
              </div>

              <!-- Live preview -->
              <div class="form-group">
                <label>Preview</label>
                <div style="padding:0.75rem;border-radius:8px;background:${this._themeDraft.backgroundColor};border:1px solid ${this._themeDraft.borderColor}">
                  <div style="font-family:${this._themeDraft.fontFamily};color:${this._themeDraft.textColor};font-size:0.85rem;font-weight:600;margin-bottom:0.35rem">
                    Sample Heading
                  </div>
                  <div style="font-family:${this._themeDraft.fontFamily};color:${this._themeDraft.textColor};font-size:0.78rem;margin-bottom:0.5rem">
                    Body text with the selected font and colours.
                  </div>
                  <div style="background:${this._themeDraft.surfaceColor};border:1px solid ${this._themeDraft.borderColor};border-radius:6px;padding:0.4rem 0.6rem;margin-bottom:0.5rem;font-family:${this._themeDraft.fontFamily}">
                    <span style="color:${this._themeDraft.textColor};font-size:0.72rem">Card / surface</span>
                  </div>
                  <button style="background:${this._themeDraft.accentColor};color:#fff;border:none;border-radius:4px;padding:4px 14px;font-size:0.75rem;cursor:default;font-family:${this._themeDraft.fontFamily}">
                    Button
                  </button>
                </div>
              </div>

              ${this._themeError ? html`<div class="modal-error">${this._themeError}</div>` : ''}
            `}
          </div>
          ${this._selectedDB?.accessAs?.includes('ET_Admin') ? html`
            <div class="modal-footer">
              <button class="btn-cancel" @click=${() => { this._themeDraft = { ...DEFAULT_THEME }; }}>Reset to Default</button>
              <button class="btn-cancel" @click=${this._closeThemeModal}>Cancel</button>
              <button class="btn-submit" ?disabled=${this._themeSaving} @click=${this._saveTheme}>
                ${this._themeSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }
}
